import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConsoleRole } from '../../session/SessionContext';
import { usePageTab } from '../../shell/Shell';
import { pillSoft, sx } from '../../ui/designStyles';
import { GuardsTab } from './GuardsTab';
import { PlaybooksTab } from './PlaybooksTab';
import { AutonomyTab } from './AutonomyTab';
import { HistoryTab } from './HistoryTab';
import { policyRoleOf } from './util';
import { tierLabel } from '../residents/common';

export const POLICY_TABS = ['Guards', 'Playbooks', 'Autonomy', 'History'] as const;
export type PolicyTab = (typeof POLICY_TABS)[number];

/**
 * Policy (design: isPolicy, T12343; policyVals L26418, policyV2 L26047). Four tabs:
 * Guards, Playbooks, Autonomy and History. Aithora (Super Admin, Operator) edits the
 * network; a Facility Admin's changes are change requests for their own building that
 * only the owner approves (the server enforces it); Dev and Partner read.
 */
export function Policy() {
  const { role } = useConsoleRole();
  const [params, setParams] = useSearchParams();
  const initial = POLICY_TABS.find((t) => t.toLowerCase() === (params.get('tab') ?? '').toLowerCase()) ?? 'Guards';
  const [tab, setTab] = useState<PolicyTab>(initial);
  usePageTab(tab);
  const pr = policyRoleOf(role);
  const pick = (t: PolicyTab) => {
    setTab(t);
    const next = new URLSearchParams(params);
    if (t === 'Guards') next.delete('tab');
    else next.set('tab', t.toLowerCase());
    setParams(next, { replace: true });
  };
  return (
    <div data-screen-label="Gate Controls" data-testid="policy-page" style={sx('display:flex;flex-direction:column;gap:0;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
      <div role="tablist" aria-label="Policy" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
        {POLICY_TABS.map((t) => (
          <a
            key={t}
            href="#"
            role="tab"
            aria-selected={tab === t}
            onClick={(e) => {
              e.preventDefault();
              pick(t);
            }}
            style={sx(pillSoft(tab === t))}
          >
            {t}
          </a>
        ))}
        {/* V9: a read-only seat sees everything, no action buttons, and says so. */}
        {pr === 'readonly' && tab !== 'Guards' && (
          <span data-testid="policy-readonly" style={sx('margin-left:6px;font-size:14px;color:#54606C;')}>
            {'Read-only as ' + tierLabel(role) + '.'}
          </span>
        )}
      </div>
      <div
        style={sx(
          tab === 'Guards'
            ? 'flex:1 1 auto;min-height:0;overflow:hidden;min-width:0;display:flex;flex-direction:column;'
            : 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;',
        )}
      >
        {tab === 'Guards' && <GuardsTab pr={pr} onHistory={() => pick('History')} />}
        {tab === 'Playbooks' && <PlaybooksTab pr={pr} />}
        {tab === 'Autonomy' && <AutonomyTab pr={pr} />}
        {tab === 'History' && <HistoryTab pr={pr} onGo={pick} />}
      </div>
    </div>
  );
}
